import { ApiError } from "../services/api-error";
import { handleApiError } from "./error-handler";
import { extractApiErrorCode } from "../services/card-gate/card-gate-controller";

/**
 * AD-384 (19.09.2026). ОТКАЗ ЗАПИСИ НА СОБЫТИЕ — ПО КОДУ, А НЕ ПО ЕГО ТЕКСТУ.
 *
 * ЧТО БЫЛО. Экраны записи (`EventRegistration`, `ParticipantDashboard`)
 * печатали отказ через `handleApiError`, а тот отдаёт `message` ответа КАК
 * ЕСТЬ. Все доменные отказы канона записи названы машинным кодом
 * (`REGISTRATION_DEADLINE_PASSED`, `EVENT_PARTICIPANT_LIMIT_REACHED`,
 * `PARTICIPANT_NOT_REGISTERED`…), поэтому в красной плашке человек читал
 * заглавные латинские буквы: ни причины, ни того, что делать дальше.
 *
 * ПОЧЕМУ ЗДЕСЬ, А НЕ В КАЖДОМ ЭКРАНЕ. Дверь в канон записи одна
 * (`eventsApi.registerForEvent` / `unregisterFromEvent`), значит и словарь её
 * отказов один. Экраны различаются только тем, из какого пространства берут
 * фразу: `events.registration.errors.*` у карточки события,
 * `dashboard.participant.registerErrors.*` у кабинета участника.
 * `EventGuestJoinPage` намеренно держит СВОЮ карту: у страницы приглашения
 * другой набор исходов (отозванное приглашение, ответ RSVP), и общего словаря
 * ей мало.
 *
 * ТАБЛИЦА ЗАКРЫТАЯ: ключ выдаётся только для известного кода, иначе — общая
 * фраза экрана. Так незнакомый отказ не превращается в уверенную ложь.
 *
 * КОДЫ ПРИХОДЯТ ДВУМЯ ДОРОГАМИ, поэтому смотрим оба места.
 *  • Исключения, назвавшие код полем (`super({ code, message })`):
 *    `EventNotOpenForRegistrationException`,
 *    `SelfRegistrationNotEnabledException`,
 *    `RegistrationDeadlinePassedException`. Фильтр BE кладёт код в `error.code`
 *    конверта ADR-002 — его достаёт `extractApiErrorCode`.
 *  • Исключения, названные строкой (`super('PARTICIPANT_NOT_REGISTERED')`):
 *    `ParticipantAlreadyRegisteredException`,
 *    `EventParticipantLimitReachedException`,
 *    `ParticipantNotRegisteredException`, `EventNotFoundException`. У них в
 *    `error.code` лежит КОНСТАНТА СТАТУСА (`BAD_REQUEST`, `NOT_FOUND`), а сам
 *    код — в сообщении, куда `HttpClient.handleError` кладёт legacy-`message`.
 */
export type RegistrationFailure =
  | "deadlinePassed"
  | "closed"
  | "alreadyRegistered"
  | "full"
  | "notRegistered"
  | "notFound"
  | "rejected";

export const REGISTRATION_FAILURE_BY_CODE: Record<string, RegistrationFailure> =
  {
    REGISTRATION_DEADLINE_PASSED: "deadlinePassed",
    EVENT_NOT_OPEN_FOR_REGISTRATION: "closed",
    SELF_REGISTRATION_NOT_ENABLED: "closed",
    PARTICIPANT_ALREADY_REGISTERED: "alreadyRegistered",
    EVENT_PARTICIPANT_LIMIT_REACHED: "full",
    PARTICIPANT_NOT_REGISTERED: "notRegistered",
    EVENT_NOT_FOUND: "notFound",
    // AD-531. Заявку отклонил или участие отменил организатор: запись его
    // решение не переписывает, и выход — тоже (`RegistrationRejectedException`,
    // код полем). Повтор даст тот же отказ, поэтому экраны снимают кнопку.
    REGISTRATION_REJECTED: "rejected",
  };

/**
 * Общий разбор: КОД ОТКАЗА ПО ЗАКРЫТОЙ ТАБЛИЦЕ, двумя дорогами сразу.
 *
 * Дороги описаны в шапке файла и от двери не зависят — они свойство того, как
 * исключение объявлено на бэкенде. Поэтому таблица параметром, а обход обеих
 * дорог один: разойдясь, две копии отличались бы тем, какая дверь понимает
 * исключение-строку, а какая нет, — и отказ выходил бы человеку кодом на одном
 * экране и фразой на соседнем.
 */
const failureByTable = <T extends string>(
  err: unknown,
  table: Record<string, T>,
): T | undefined => {
  const code = extractApiErrorCode(err);
  const byCode = code ? table[code] : undefined;
  if (byCode) return byCode;
  if (err instanceof ApiError) {
    return table[err.message];
  }
  return undefined;
};

/** Исход записи по ошибке сети, либо `undefined` — если код экрану незнаком. */
export const registrationFailureOf = (
  err: unknown,
): RegistrationFailure | undefined =>
  failureByTable(err, REGISTRATION_FAILURE_BY_CODE);

/**
 * AD-389 шаг 6. ОТКАЗ СМЕНЫ СПОСОБА УЧАСТИЯ — ОТДЕЛЬНАЯ ДВЕРЬ, ОТДЕЛЬНЫЙ
 * СЛОВАРЬ.
 *
 * `PATCH /events/:id/participation-mode` — не канон записи: он ничего не
 * записывает и не отменяет, и его отказы человеку означают другое. Дописать
 * два кода в таблицу выше значило бы выдать их всем экранам записи —
 * `ParticipantDashboard` получил бы исходы, которых его дверь не возвращает, и
 * обязан был бы завести под них фразы в своём пространстве. Разделение держит
 * каждый словарь равным набору исходов СВОЕЙ двери.
 *
 * `PARTICIPANT_NOT_FOUND` здесь значит «вы на это событие не записаны»: менять
 * способ до записи нечему, и это не ошибка сервера, а состояние, которое стоит
 * назвать словами.
 */
export type ParticipationModeFailure =
  | "notApplicable"
  | "notChangeable"
  | "notRegistered"
  | "notFound";

export const PARTICIPATION_MODE_FAILURE_BY_CODE: Record<
  string,
  ParticipationModeFailure
> = {
  PARTICIPATION_MODE_NOT_APPLICABLE: "notApplicable",
  PARTICIPATION_MODE_NOT_CHANGEABLE: "notChangeable",
  PARTICIPANT_NOT_FOUND: "notRegistered",
  EVENT_NOT_FOUND: "notFound",
};

/** Исход смены способа, либо `undefined` — если код экрану незнаком. */
export const participationModeFailureOf = (
  err: unknown,
): ParticipationModeFailure | undefined =>
  failureByTable(err, PARTICIPATION_MODE_FAILURE_BY_CODE);

/**
 * AD-413 (20.09.2026). ОТКАЗ САМОПОДТВЕРЖДЕНИЯ — ТРЕТЬЯ ДВЕРЬ, ТРЕТИЙ СЛОВАРЬ.
 *
 * `POST /events/:id/participation/confirm` не записывает и не отменяет запись:
 * он переводит СВОЁ участие `registered → confirmed`. Его набор исходов свой, и
 * дописать эти коды в таблицу записи значило бы выдать их всем экранам записи —
 * тем, чья дверь таких отказов не возвращает (тот же довод, что у таблицы
 * смены способа участия).
 *
 * `PAID_PARTICIPATION_CONFIRMED_BY_ORGANIZER` здесь не «ошибка», а правило:
 * на платном событии в `confirmed` переводит организатор, получив деньги
 * (решение владельца 20.09.2026). Кнопка на платном событии и не показывается —
 * код разбирается на случай, когда цена события изменилась между чтением
 * карточки и нажатием.
 */
export type ConfirmParticipationFailure =
  | "notConfirmable"
  | "paidByOrganizer"
  | "notRegistered"
  | "notFound";

export const CONFIRM_PARTICIPATION_FAILURE_BY_CODE: Record<
  string,
  ConfirmParticipationFailure
> = {
  PARTICIPATION_NOT_CONFIRMABLE: "notConfirmable",
  PAID_PARTICIPATION_CONFIRMED_BY_ORGANIZER: "paidByOrganizer",
  PARTICIPANT_NOT_FOUND: "notRegistered",
  EVENT_NOT_FOUND: "notFound",
};

/** Исход подтверждения участия, либо `undefined` — если код экрану незнаком. */
export const confirmParticipationFailureOf = (
  err: unknown,
): ConfirmParticipationFailure | undefined =>
  failureByTable(err, CONFIRM_PARTICIPATION_FAILURE_BY_CODE);

/**
 * Машинный код в ответе узнаётся по форме: заглавные латинские буквы, цифры и
 * хотя бы одно подчёркивание (`EVENT_NOT_OPEN_FOR_REGISTRATION`). Ни одна фраза
 * для человека так не выглядит.
 */
const MACHINE_CODE_RE = /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+$/;

/**
 * Что показать, когда код отказа таблице незнаком.
 *
 * Сообщение ответа отбрасывается ТОЛЬКО если оно само — машинный код: именно
 * такие строки и выходили человеку в лицо (AD-384). Осмысленный текст —
 * «Не удалось подключиться к серверу…», разбор ошибок валидации, ответ нового
 * отказа, которого здесь ещё нет, — доходит как раньше: он полезнее общей
 * фразы, и терять его вместе с кодами было бы платой не по делу.
 */
export const registrationFallbackMessage = (
  err: unknown,
  generic: string,
): string => {
  const text = handleApiError(err, generic);
  return MACHINE_CODE_RE.test(text.trim()) ? generic : text;
};
